import { useState, useCallback } from 'react'
import { generateLocationReference } from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 15000

/**
 * Custom hook to manage location reference generation and polling
 * Handles location map generation, editing, and batch updates
 */
export const useLocationReferences = ({
  formData,
  setFormData,
  videoId
} = {}) => {
  const {
    isPolling,
    progress: locationGenerationProgress,
    status: locationGenerationStatus,
    startPolling
  } = useTaskPoller({ pollInterval: POLL_INTERVAL })

  const [isRequesting, setIsRequesting] = useState(false)

  /**
   * Generate location map references
   */
  const generateLocationMap = useCallback(
    async (locationMapObj, existingUris) => {
      setIsRequesting(true)
      try {
        const locationRes = await generateLocationReference({
          location_map_with_prompts: locationMapObj,
          existing_location_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.locationReference,
          video_id: videoId
        })
        const taskId =
          locationRes && locationRes.data && locationRes.data.task_id
        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              locationReference: result.output
            }))
          }
        }
      } finally {
        setIsRequesting(false)
      }
    },
    [setFormData, startPolling, videoId]
  )

  /**
   * Add new location reference
   */
  const handleAddLocationReference = useCallback(
    async newLocationMap => {
      try {
        const locationRes = await generateLocationReference({
          location_map_with_prompts: {
            ...(formData?.locationMap || {}),
            ...newLocationMap
          },
          existing_location_uris: formData?.locationReference,
          video_id: videoId
        })
        const taskId =
          locationRes && locationRes.data && locationRes.data.task_id
        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...result.output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...newLocationMap
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error adding location reference:', error)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      setFormData,
      startPolling,
      videoId
    ]
  )

  /**
   * Edit existing location reference
   */
  const handleEditLocationReference = useCallback(
    async editingLocationPrompt => {
      try {
        const editingKeys = Object.keys(editingLocationPrompt)

        // Remove old URIs for edited locations
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        const locationRes = await generateLocationReference({
          location_map_with_prompts: {
            ...(formData?.locationMap || {}),
            ...editingLocationPrompt
          },
          existing_location_uris: filteredLocationUris,
          video_id: videoId
        })

        const taskId = locationRes?.data?.task_id

        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...result.output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...editingLocationPrompt
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error editing location reference:', error)
      }
    },
    [
      formData?.locationMap,
      formData?.locationReference,
      setFormData,
      startPolling,
      videoId
    ]
  )

  /**
   * Batch update multiple locations
   */
  const handleBatchUpdateLocations = useCallback(
    async (updatedLocationMap, modifiedKeys) => {
      try {
        // Filter out URIs for modified keys
        const filteredLocationUris = Object.keys(
          formData?.locationReference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.locationReference[key]
          }
          return acc
        }, {})

        const locationRes = await generateLocationReference({
          location_map_with_prompts: updatedLocationMap,
          existing_location_uris: filteredLocationUris,
          video_id: videoId
        })

        const taskId = locationRes?.data?.task_id

        if (taskId) {
          const result = await startPolling(taskId)
          if (result && result.status === 'success' && result.output) {
            setFormData(prev => ({
              ...prev,
              locationReference: {
                ...(prev.locationReference || {}),
                ...result.output
              },
              locationMap: {
                ...(prev.locationMap || {}),
                ...updatedLocationMap
              }
            }))
          }
        }
      } catch (error) {
        console.error('Error batch updating location references:', error)
      }
    },
    [formData?.locationReference, setFormData, startPolling, videoId]
  )

  return {
    // State
    isGeneratingLocationReference: isPolling || isRequesting,
    locationGenerationProgress,
    locationGenerationStatus,

    // Actions
    generateLocationMap,
    handleAddLocationReference,
    handleEditLocationReference,
    handleBatchUpdateLocations
  }
}

export default useLocationReferences
